import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { motion } from 'framer-motion';
import { WordMark } from '../../components/WordMark';
import {
  listFriends,
  searchPlayers,
  requestFriend,
  acceptFriend,
  rejectFriend,
  removeFriend,
  createInvite,
  redeemInvite,
  inviteLink,
  type FriendsList,
  type FriendSummary,
  type PlayerSearchResult,
  type RedeemResult,
  type FriendRequestResult,
} from '../../net/friendsClient';
import { useAccountStore } from '../../stores/account';
import { EDGES } from '../../theme/tokens';
import { PlayerActionsSheet, type PlayerRef } from '../../components/PlayerActions';

// Friend system Phase 2 — list, search, add-by-handle, accept/reject,
// invite-link share + auto-redeem. "Challenge" CTA is intentionally
// scaffold-only here; the turn-based PvP flow it triggers ships in
// Phase 3.

type Toast = { tone: 'good' | 'bad'; text: string };

// NOTE: project tsconfig has strict: off, which means discriminated-
// union narrowing on `result.ok` doesn't refine the union. We cast
// through `any` after the explicit check — runtime is correct, TS just
// can't see it. Switching the whole project to strict is a larger
// refactor for later.
function describeRedeem(result: RedeemResult): Toast {
  if (result.ok) return { tone: 'good', text: 'Invite accepted — friend added.' };
  const code = (result as { code: string }).code;
  if (code === 'self_redeem') return { tone: 'bad', text: 'That invite was yours — share it instead.' };
  if (code === 'invalid_or_expired') return { tone: 'bad', text: 'Invite expired or already used.' };
  return { tone: 'bad', text: 'Could not reach the server.' };
}

function describeRequest(result: FriendRequestResult, handle: string): { good: string | null; bad: string | null } {
  if (result.ok) return { good: `Request sent to ${handle}.`, bad: null };
  const code = (result as { code: string }).code;
  if (code === 'already_exists') return { good: null, bad: 'Already friends or request pending.' };
  if (code === 'unknown_player') return { good: null, bad: 'Player not found.' };
  return { good: null, bad: 'Could not reach the server.' };
}

function formatLastSeen(iso: string | null): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (ms < 5 * minute) return 'online';
  if (ms < hour) return `${Math.floor(ms / minute)}m ago`;
  if (ms < day) return `${Math.floor(ms / hour)}h ago`;
  return `${Math.floor(ms / day)}d ago`;
}

export function FriendsPage() {
  const [params, setParams] = useSearchParams();
  const handle = useAccountStore((s) => s.handle);

  const [data, setData] = useState<FriendsList | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null);
  // Which player the report/block sheet is open for, if any.
  const [flagged, setFlagged] = useState<PlayerRef | null>(null);

  // Initial fetch + auto-redeem flow.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const inviteToken = params.get('invite');
      if (inviteToken) {
        const result = await redeemInvite(inviteToken);
        const toastMsg = describeRedeem(result);
        if (!cancelled) {
          setToast(toastMsg);
          // Strip the query param so reload doesn't redeem twice.
          const next = new URLSearchParams(params);
          next.delete('invite');
          setParams(next, { replace: true });
        }
      }
      const list = await listFriends();
      if (!cancelled) {
        setData(list);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refresh() {
    const list = await listFriends();
    setData(list);
  }

  async function handleAccept(id: string) {
    const ok = await acceptFriend(id);
    setToast({
      tone: ok ? 'good' : 'bad',
      text: ok ? 'Friend added.' : 'Could not accept request.',
    });
    if (ok) refresh();
  }

  async function handleReject(id: string) {
    const ok = await rejectFriend(id);
    if (ok) refresh();
  }

  async function handleRemove(id: string) {
    if (!confirm('Remove this friend?')) return;
    const ok = await removeFriend(id);
    if (ok) refresh();
  }

  function handleFlagDone(what: 'reported' | 'blocked', ok: boolean) {
    if (!ok) {
      setToast({ tone: 'bad', text: 'Could not reach the server. Try again.' });
      return;
    }
    setToast({
      tone: 'good',
      text: what === 'blocked' ? 'Player blocked. Undo in Settings.' : 'Report sent. Thank you.',
    });
    if (what === 'blocked') refresh();
  }

  // Auto-clear toasts after a few seconds.
  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(id);
  }, [toast]);

  return (
    <main className="pt-safe relative h-dvh w-full overflow-hidden bg-asphalt">
      <div
        aria-hidden
        className="pointer-events-none absolute -right-1/4 -top-1/4 h-[60vw] w-[60vw] max-h-[640px] max-w-[640px]"
        style={{
          background:
            'radial-gradient(circle, rgba(255,138,61,0.14) 0%, rgba(255,138,61,0) 70%)',
        }}
      />

      <section className="relative mx-auto flex h-full w-full max-w-5xl flex-col gap-md overflow-hidden px-md py-sm md:gap-lg md:px-lg md:py-md lg:max-w-6xl lg:gap-xl lg:py-xl">
        <header className="flex items-center justify-between">
          <span className="md:hidden">
            <WordMark size="sm" />
          </span>
          <span className="hidden md:inline lg:hidden">
            <WordMark size="md" />
          </span>
          <span className="hidden lg:inline">
            <WordMark size="lg" />
          </span>
          <Link
            to="/menu"
            className="font-data text-[12px] uppercase tracking-[0.3em] text-graphite hover:text-marker md:text-[12px] lg:text-[14px]"
          >
            ← menu
          </Link>
        </header>

        <div className="flex items-baseline justify-between gap-md">
          <div>
            <p className="font-data text-[12px] uppercase tracking-[0.3em] text-marker md:text-[12px]">
              /friends
            </p>
            <h1 className="mt-xs font-cholo text-[28px] leading-none text-sodium md:text-[48px] lg:text-[72px]">
              The crew
            </h1>
          </div>
          {data && (
            <span className="font-data text-[13px] uppercase tracking-[0.2em] text-graphite md:text-[13px]">
              {data.mutual.length} mutual
              {data.incoming.length > 0 ? ` · ${data.incoming.length} pending` : ''}
            </span>
          )}
        </div>

        {toast && (
          <p
            role="status"
            className={`rounded-sm border px-md py-sm font-data text-[13px] uppercase tracking-[0.2em] md:text-[12px] ${
              toast.tone === 'good'
                ? 'border-glowStick/40 bg-glowStick/10 text-glowStick'
                : 'border-aerosolRed/40 bg-aerosolRed/10 text-aerosolRed'
            }`}
          >
            {toast.text}
          </p>
        )}

        <div className="flex flex-1 flex-col gap-md overflow-y-auto md:flex-row md:gap-lg md:overflow-hidden">
          <div className="flex flex-col gap-md md:w-[340px] md:shrink-0 lg:w-[380px]">
            <AddFriend
              myHandle={handle}
              onFlag={setFlagged}
              onAdded={(text) => {
                setToast({ tone: 'good', text });
                refresh();
              }}
              onError={(text) => setToast({ tone: 'bad', text })}
            />
            <InviteLinkPanel
              onError={() => setToast({ tone: 'bad', text: 'Could not create invite.' })}
            />
          </div>

          <div className="flex flex-1 flex-col gap-md md:overflow-y-auto md:pr-xs">
            {loading && <p className="font-data text-[12px] uppercase tracking-[0.2em] text-graphite">loading…</p>}

            {!loading && !data && (
              <p
                className="rounded-sm border border-aerosolRed/40 bg-aerosolRed/10 px-md py-md font-data text-[13px] uppercase tracking-[0.2em] text-aerosolRed md:text-[12px]"
                role="status"
              >
                Could not reach the server. Friends will sync when you're back online.
              </p>
            )}

            {!loading && data && data.incoming.length > 0 && (
              <Section title={`Pending · ${data.incoming.length}`} tone="sodium">
                {data.incoming.map((f) => (
                  <FriendCard
                    key={f.id}
                    friend={f}
                    actions={
                      <>
                        <CardButton tone="sodium" onClick={() => handleAccept(f.id)}>accept</CardButton>
                        <CardButton tone="ghost" onClick={() => handleReject(f.id)}>reject</CardButton>
                        <CardButton tone="ghost" onClick={() => setFlagged(f)} title="Report or block">⚑</CardButton>
                      </>
                    }
                  />
                ))}
              </Section>
            )}

            {!loading && data && data.outgoing.length > 0 && (
              <Section title={`Sent · ${data.outgoing.length}`} tone="graphite">
                {data.outgoing.map((f) => (
                  <FriendCard
                    key={f.id}
                    friend={f}
                    subtitleOverride="waiting…"
                    actions={
                      <CardButton tone="ghost" onClick={() => handleRemove(f.id)}>cancel</CardButton>
                    }
                  />
                ))}
              </Section>
            )}

            {!loading && data && (
              <Section title={`Crew · ${data.mutual.length}`} tone="cholo">
                {data.mutual.length === 0 ? (
                  <p className="rounded-sm border border-dashed bg-stucco1/40 px-md py-md text-center font-data text-[13px] uppercase tracking-[0.2em] text-graphite md:text-[12px]" style={{ borderColor: EDGES.faint }}>
                    no crew yet — search by handle or share an invite link
                  </p>
                ) : (
                  data.mutual.map((f) => (
                    <FriendCard
                      key={f.id}
                      friend={f}
                      actions={
                        <>
                          {/* Challenge is the Phase 3 entry point — wired
                              as scaffold so the button is in place when
                              turn-based ships. */}
                          <CardButton
                            tone="sodium"
                            disabled
                            title="Turn-based PvP ships in Phase 3"
                          >
                            challenge ›
                          </CardButton>
                          <CardButton tone="ghost" onClick={() => handleRemove(f.id)}>remove</CardButton>
                          <CardButton tone="ghost" onClick={() => setFlagged(f)} title="Report or block">⚑</CardButton>
                        </>
                      }
                    />
                  ))
                )}
              </Section>
            )}
          </div>
        </div>
      </section>
      {flagged && (
        <PlayerActionsSheet
          player={flagged}
          onClose={() => setFlagged(null)}
          onDone={handleFlagDone}
        />
      )}
    </main>
  );
}

interface SectionProps {
  title: string;
  tone: 'sodium' | 'cholo' | 'graphite';
  children: React.ReactNode;
}

function Section({ title, tone, children }: SectionProps) {
  const toneText =
    tone === 'sodium' ? 'text-sodium' : tone === 'cholo' ? 'text-cholo' : 'text-graphite';
  return (
    <div className="flex flex-col gap-xs">
      <h2 className={`font-data text-[12px] uppercase tracking-[0.3em] ${toneText} md:text-[12px]`}>
        {title}
      </h2>
      <div className="flex flex-col gap-xs md:gap-sm">{children}</div>
    </div>
  );
}

function FriendCard({
  friend,
  actions,
  subtitleOverride,
}: {
  friend: FriendSummary;
  actions: React.ReactNode;
  subtitleOverride?: string;
}) {
  return (
    <div
      className="flex items-center justify-between gap-md rounded-sm border bg-stucco1 px-sm py-xs md:px-md md:py-sm"
      style={{ borderColor: EDGES.soft }}
    >
      <div className="flex min-w-0 flex-col">
        <span className="truncate font-cholo text-[16px] leading-tight text-bone md:text-[18px]">
          {friend.handle || `Player ${friend.id.slice(0, 6)}`}
        </span>
        <span className="font-data text-[12px] uppercase tracking-[0.2em] text-graphite md:text-[11px]">
          {subtitleOverride ?? `${formatLastSeen(friend.lastSeenAt)} · ${friend.rating} elo`}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-xs">{actions}</div>
    </div>
  );
}

function CardButton({
  children,
  onClick,
  tone,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  tone: 'sodium' | 'ghost';
  disabled?: boolean;
  title?: string;
}) {
  const base =
    tone === 'sodium'
      ? 'bg-sodium text-sharpie hover:bg-sodiumGlow'
      : 'border border-stucco3 text-marker hover:text-bone';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`min-h-[44px] rounded-sm px-sm py-[4px] font-data text-[12px] uppercase tracking-[0.2em] transition-colors md:min-h-[36px] md:px-md md:text-[11px] ${base} ${
        disabled ? 'cursor-not-allowed opacity-50' : ''
      }`}
    >
      {children}
    </button>
  );
}

function AddFriend({
  myHandle,
  onAdded,
  onError,
  onFlag,
}: {
  myHandle: string;
  onAdded: (text: string) => void;
  onError: (text: string) => void;
  onFlag: (p: PlayerRef) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PlayerSearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  // Debounced search.
  const debounced = useDebouncedQuery(query, 250);
  useEffect(() => {
    let cancelled = false;
    if (debounced.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    searchPlayers(debounced).then((r) => {
      if (!cancelled) {
        setResults(r);
        setSearching(false);
      }
    });
    return () => { cancelled = true; };
  }, [debounced]);

  async function handleSendRequest(target: PlayerSearchResult) {
    const result = await requestFriend(target.id);
    const labels = describeRequest(result, target.handle || target.id.slice(0, 8));
    if (labels.good) {
      onAdded(labels.good);
      setQuery('');
      setResults([]);
    } else if (labels.bad) {
      onError(labels.bad);
    }
  }

  const handleMissing = !myHandle || myHandle.startsWith('Player ');

  return (
    <div
      className="flex flex-col gap-sm rounded-md border bg-stucco1 p-md md:p-lg"
      style={{ borderColor: EDGES.soft }}
    >
      <p className="font-data text-[12px] uppercase tracking-[0.3em] text-marker md:text-[12px]">
        find a friend
      </p>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="handle (min 2 chars)"
        className="rounded-sm border bg-asphalt px-md py-sm font-data text-[13px] text-bone outline-none placeholder:text-graphite focus:border-sodium md:text-[14px]"
        style={{ borderColor: 'rgba(245,236,214,0.15)' }}
        maxLength={24}
      />
      {handleMissing && (
        <p className="font-data text-[12px] uppercase tracking-[0.2em] text-graphite md:text-[11px]">
          set a handle in /profile so friends can find you back.
        </p>
      )}
      {searching && (
        <p className="font-data text-[12px] uppercase tracking-[0.2em] text-graphite md:text-[11px]">
          searching…
        </p>
      )}
      {results.length > 0 && (
        <div className="flex flex-col gap-xs">
          {results.map((r) => (
            <div key={r.id} className="flex items-stretch gap-xs">
            <button
              onClick={() => handleSendRequest(r)}
              className="flex flex-1 items-center justify-between gap-sm rounded-sm border border-stucco3 bg-stucco2 px-sm py-xs text-left transition-colors hover:bg-stucco3"
            >
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-cholo text-[14px] text-bone md:text-[16px]">
                  {r.handle}
                </span>
                <span className="font-data text-[13px] uppercase tracking-[0.2em] text-graphite md:text-[10px]">
                  {formatLastSeen(r.lastSeenAt)}
                </span>
              </span>
              <span className="font-data text-[12px] uppercase tracking-[0.2em] text-sodium md:text-[11px]">
                add ›
              </span>
            </button>
            <button
              onClick={() => onFlag(r)}
              title="Report or block"
              aria-label={`Report or block ${r.handle ?? 'player'}`}
              className="min-h-[44px] rounded-sm border border-stucco3 px-sm font-data text-[12px] text-marker hover:text-bone"
            >
              ⚑
            </button>
            </div>
          ))}
        </div>
      )}
      {!searching && query.length >= 2 && results.length === 0 && (
        <p className="font-data text-[12px] uppercase tracking-[0.2em] text-graphite md:text-[11px]">
          no matches — try a different handle
        </p>
      )}
    </div>
  );
}

function InviteLinkPanel({ onError }: { onError: () => void }) {
  const [token, setToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const link = useMemo(() => (token ? inviteLink(token) : null), [token]);

  async function handleGenerate() {
    const t = await createInvite();
    if (!t) {
      onError();
      return;
    }
    setToken(t);
    setCopied(false);
  }

  async function handleCopy() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard rejected (insecure context, etc.) — fall through.
    }
  }

  return (
    <div
      className="flex flex-col gap-sm rounded-md border bg-stucco1 p-md md:p-lg"
      style={{ borderColor: EDGES.soft }}
    >
      <p className="font-data text-[12px] uppercase tracking-[0.3em] text-marker md:text-[12px]">
        share invite link
      </p>
      {!link ? (
        <>
          <p className="text-[12px] text-marker md:text-[13px]">
            Single-use link, expires in 14 days. Send it in any chat — opening it adds the friend instantly.
          </p>
          <motion.button
            onClick={handleGenerate}
            whileTap={{ scale: 0.97 }}
            className="min-h-[44px] rounded-sm bg-sodium px-md font-data text-[13px] uppercase tracking-[0.2em] text-sharpie hover:bg-sodiumGlow md:min-h-[44px] md:text-[13px]"
          >
            generate link
          </motion.button>
        </>
      ) : (
        <>
          <p className="break-all rounded-sm border border-stucco3 bg-asphalt px-sm py-xs font-data text-[13px] text-bone md:text-[12px]">
            {link}
          </p>
          <div className="flex gap-xs">
            <button
              onClick={handleCopy}
              className="min-h-[44px] flex-1 rounded-sm bg-sodium px-md font-data text-[13px] uppercase tracking-[0.2em] text-sharpie hover:bg-sodiumGlow md:text-[12px]"
            >
              {copied ? 'copied ✓' : 'copy link'}
            </button>
            <button
              onClick={handleGenerate}
              className="min-h-[44px] rounded-sm border border-stucco3 px-md font-data text-[13px] uppercase tracking-[0.2em] text-marker hover:text-bone md:text-[12px]"
            >
              new
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function useDebouncedQuery(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}
